import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ROCKET_COLORS, STAGE_ICONS, SHIP_VARIANTS } from "@/constants";
import { applyProgress, updateProject, deleteProject, setActiveProject, setAutoDelete } from "@/lib/api";
import Rocket from "@/components/Rocket";
import { Plus, Trash2, X, ArrowUp, ArrowDown, Sparkles, RotateCcw, Crown, Image as ImageIcon } from "lucide-react";

export default function TeacherTools({ open, onOpenChange, room, onChange, onRoom }) {
  const [newPupil, setNewPupil] = useState("");
  const [newPupilColor, setNewPupilColor] = useState(ROCKET_COLORS[1].hex);
  const [newPupilVariant, setNewPupilVariant] = useState("rocket");
  const [newStage, setNewStage] = useState("");
  const [newStageIcon, setNewStageIcon] = useState("🪐");

  // roomRef always mirrors the latest room so rapid successive edits (blur → blur)
  // are computed from up-to-date state and never overwrite each other. Declared
  // before the early return to satisfy react-hooks/rules-of-hooks.
  const roomRef = useRef(room);
  useEffect(() => { roomRef.current = room; }, [room]);

  if (!room) return null;

  const commit = (patch) => onChange({ ...room, ...patch });

  // ---- Pupils ----
  const addPupil = () => {
    const name = newPupil.trim();
    if (!name) return;
    const pupils = [
      ...room.pupils,
      {
        id: crypto.randomUUID(),
        name,
        color: newPupilColor,
        variant: newPupilVariant,
        stageIndex: 0,
      },
    ];
    commit({ pupils });
    setNewPupil("");
  };

  const removePupil = (id) =>
    commit({ pupils: room.pupils.filter((p) => p.id !== id) });

  const setPupilColor = (id, color) =>
    commit({ pupils: room.pupils.map((p) => (p.id === id ? { ...p, color } : p)) });

  const cyclePupilVariant = (id) =>
    commit({
      pupils: room.pupils.map((p) => {
        if (p.id !== id) return p;
        const idx = SHIP_VARIANTS.findIndex((v) => v.id === (p.variant || "rocket"));
        return { ...p, variant: SHIP_VARIANTS[(idx + 1) % SHIP_VARIANTS.length].id };
      }),
    });

  // ---- Stages ----
  const addStage = () => {
    const name = newStage.trim();
    if (!name) return;
    const stages = [
      ...room.stages,
      { id: crypto.randomUUID(), name, color: "#A855F7", icon: newStageIcon },
    ];
    commit({ stages });
    setNewStage("");
  };

  const removeStage = (idx) => {
    if (room.stages.length <= 2) return;
    const stages = room.stages.filter((_, i) => i !== idx);
    const pupils = room.pupils.map((p) => ({
      ...p,
      stageIndex: Math.min(p.stageIndex, stages.length - 1),
    }));
    commit({ stages, pupils });
  };

  const renameStage = (idx, name) =>
    commit({ stages: room.stages.map((s, i) => (i === idx ? { ...s, name } : s)) });

  const setStageIcon = (idx, icon) =>
    commit({ stages: room.stages.map((s, i) => (i === idx ? { ...s, icon } : s)) });

  const moveStage = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= room.stages.length) return;
    const stages = [...room.stages];
    [stages[idx], stages[j]] = [stages[j], stages[idx]];
    commit({ stages });
  };

  // ---- Quick tools ----
  const moveAll = (stageIndex) =>
    commit({ pupils: room.pupils.map((p) => applyProgress(p, stageIndex)) });

  // ---- Admin editing ----
  const setPupilName = (id, name) =>
    commit({ pupils: room.pupils.map((p) => (p.id === id ? { ...p, name } : p)) });

  const setPupilStage = (id, stageIndex) =>
    commit({ pupils: room.pupils.map((p) => (p.id === id ? applyProgress(p, stageIndex) : p)) });

  // ---- Assessment + rubric editing (active project) ----
  const activeProject = (room.projects || []).find((p) => p.id === room.activeProjectId) || {};
  const questions = activeProject.assessment || [];
  const criteria = activeProject.rubric || [];

  // roomRef always mirrors the latest room so rapid successive edits (blur → blur)
  // are computed from up-to-date state and never overwrite each other.
  const curProject = () => (roomRef.current.projects || []).find((p) => p.id === roomRef.current.activeProjectId) || {};

  // Apply a project field change: update local state optimistically from latest, then persist.
  const applyProjectPatch = (patch) => {
    const latest = roomRef.current;
    const updated = { ...latest, projects: (latest.projects || []).map((p) => (p.id === latest.activeProjectId ? { ...p, ...patch } : p)) };
    roomRef.current = updated;
    onRoom && onRoom(updated);
    updateProject(latest.id, latest.activeProjectId, patch).catch(() => {});
  };
  const saveAssessment = (qs) => applyProjectPatch({ assessment: qs });
  const saveRubric = (cs) => applyProjectPatch({ rubric: cs });
  const saveAttempts = (n) => applyProjectPatch({ attemptsAllowed: n });

  const addQuestion = () => { const qs = curProject().assessment || []; if (qs.length >= 10) return; saveAssessment([...qs, { id: crypto.randomUUID(), text: "New question", marks: 1, options: [{ id: crypto.randomUUID(), text: "Option 1", correct: true }, { id: crypto.randomUUID(), text: "Option 2", correct: false }] }]); };
  const patchQuestion = (qid, patch) => saveAssessment((curProject().assessment || []).map((q) => (q.id === qid ? { ...q, ...patch } : q)));
  const removeQuestion = (qid) => saveAssessment((curProject().assessment || []).filter((q) => q.id !== qid));
  const addOption = (qid) => saveAssessment((curProject().assessment || []).map((q) => (q.id === qid ? { ...q, options: [...q.options, { id: crypto.randomUUID(), text: "New option", correct: false }] } : q)));
  const patchOption = (qid, oid, patch) => saveAssessment((curProject().assessment || []).map((q) => (q.id === qid ? { ...q, options: q.options.map((o) => (o.id === oid ? { ...o, ...patch } : o)) } : q)));
  const removeOption = (qid, oid) => saveAssessment((curProject().assessment || []).map((q) => (q.id === qid ? { ...q, options: q.options.filter((o) => o.id !== oid) } : q)));
  const fileToDataUrl = (file, cb) => { const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };
  const defaultLevels = () => [
    { id: crypto.randomUUID(), label: "Excellence", points: 4, descriptor: "" },
    { id: crypto.randomUUID(), label: "Achieving", points: 3, descriptor: "" },
    { id: crypto.randomUUID(), label: "Improving", points: 2, descriptor: "" },
    { id: crypto.randomUUID(), label: "Beginning", points: 1, descriptor: "" },
  ];
  const addCriterion = () => saveRubric([...(curProject().rubric || []), { id: crypto.randomUUID(), text: "New standard", levels: defaultLevels() }]);
  const patchCriterion = (cid, text) => saveRubric((curProject().rubric || []).map((c) => (c.id === cid ? { ...c, text } : c)));
  const removeCriterion = (cid) => saveRubric((curProject().rubric || []).filter((c) => c.id !== cid));
  const patchLevel = (cid, lid, patch) => saveRubric((curProject().rubric || []).map((c) => (c.id === cid ? { ...c, levels: (c.levels || []).map((l) => (l.id === lid ? { ...l, ...patch } : l)) } : c)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="teacher-tools-modal"
        className="max-w-2xl bg-[#141738] border-[#252B5C] text-white max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-cyan-300 flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Mission Control
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Manage astronauts, mission stages and quick class controls.
          </DialogDescription>
        </DialogHeader>

        {/* Class settings: name + teacher pace-setter */}
        <div className="space-y-3 border border-[#252B5C] rounded-xl p-3 bg-[#0E1126]">
          <div>
            <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Class name</label>
            <Input
              data-testid="edit-class-name-input"
              value={room.name}
              onChange={(e) => commit({ name: e.target.value })}
              className="bg-[#141738] border-[#252B5C] text-white mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-amber-400 font-semibold flex items-center gap-1">
              <Crown className="w-3.5 h-3.5" /> Teacher (pace-setter)
            </label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span data-testid="tools-teacher-name" className="font-heading font-semibold text-amber-300">👑 {room.teacherName}</span>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-amber-400 font-semibold flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Auto-delete this class
            </label>
            <p className="text-[11px] text-slate-400 mt-0.5">Set an end-of-year date to automatically erase all pupil names &amp; progress. Leave blank to keep until you delete it.</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <input
                data-testid="auto-delete-date"
                type="date"
                defaultValue={room.autoDeleteAt || ""}
                onChange={async (e) => { const d = await setAutoDelete(room.id, e.target.value); onRoom && onRoom(d); }}
                className="bg-[#141738] border border-[#252B5C] text-white rounded-md h-9 px-2 text-sm"
              />
              {room.autoDeleteAt && (
                <button
                  data-testid="auto-delete-clear"
                  onClick={async () => { const d = await setAutoDelete(room.id, ""); onRoom && onRoom(d); }}
                  className="text-xs text-slate-400 hover:text-white underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Projects (timelines)</label>
            <div className="space-y-1 mt-1">
              {(room.projects || []).map((pr) => (
                <div key={pr.id} data-testid={`manage-project-${pr.id}`} className="flex items-center gap-2">
                  <Input
                    data-testid={`rename-project-${pr.id}`}
                    defaultValue={pr.name}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (v && v !== pr.name) {
                        const d = await updateProject(room.id, pr.id, { name: v });
                        onRoom && onRoom(d);
                      }
                    }}
                    className="flex-1 h-8 bg-[#141738] border-[#252B5C] text-white text-sm"
                  />
                  {pr.id === room.activeProjectId && <span className="text-[10px] text-cyan-300 font-semibold">active</span>}
                  <Button
                    data-testid={`delete-project-${pr.id}`}
                    size="icon"
                    variant="ghost"
                    disabled={(room.projects || []).length <= 1}
                    onClick={async () => {
                      const d = await deleteProject(room.id, pr.id);
                      onRoom && onRoom(d);
                    }}
                    className="h-7 w-7 text-red-400 hover:bg-red-500/10 disabled:opacity-30"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Tabs defaultValue="pupils">
          <TabsList className="bg-[#0E1126] border border-[#252B5C]">
            <TabsTrigger data-testid="manage-pupils-tab" value="pupils">
              Astronauts
            </TabsTrigger>
            <TabsTrigger data-testid="manage-stages-tab" value="stages">
              Mission Stages
            </TabsTrigger>
            <TabsTrigger data-testid="manage-test-tab" value="test">
              Test
            </TabsTrigger>
            <TabsTrigger data-testid="manage-rubric-tab" value="rubric">
              Rubric
            </TabsTrigger>
            <TabsTrigger data-testid="quick-tools-tab" value="quick">
              Quick Tools
            </TabsTrigger>
          </TabsList>

          {/* PUPILS */}
          <TabsContent value="pupils" className="space-y-4 pt-4">
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                data-testid="pupil-name-input"
                value={newPupil}
                onChange={(e) => setNewPupil(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addPupil()}
                placeholder="New astronaut name"
                className="flex-1 min-w-[160px] bg-[#0E1126] border-[#252B5C] text-white"
              />
              <div className="flex gap-1" data-testid="pupil-color-select">
                {ROCKET_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    onClick={() => setNewPupilColor(c.hex)}
                    title={c.name}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      newPupilColor === c.hex ? "border-white scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
              <Button
                data-testid="add-pupil-btn"
                onClick={addPupil}
                className="bg-pink-500 hover:bg-pink-600 min-h-[44px]"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="new-pupil-variant-select">
              <span className="text-xs uppercase tracking-wider text-cyan-400 font-semibold mr-1">Ship</span>
              {SHIP_VARIANTS.map((v) => (
                <button
                  key={v.id}
                  data-testid={`new-variant-${v.id}`}
                  onClick={() => setNewPupilVariant(v.id)}
                  title={v.name}
                  className={`rounded-lg p-0.5 border-2 transition-all ${
                    newPupilVariant === v.id ? "border-cyan-400 bg-cyan-400/10" : "border-[#252B5C] bg-[#0E1126]"
                  }`}
                >
                  <Rocket color={newPupilColor} variant={v.id} size={34} flame={false} />
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {room.pupils.length === 0 && (
                <p className="text-slate-400 text-sm">No astronauts yet — add your first crew member above!</p>
              )}
              {room.pupils.map((p) => (
                <div
                  key={p.id}
                  data-testid={`manage-pupil-${p.id}`}
                  className="flex items-center gap-3 bg-[#0E1126] border border-[#252B5C] rounded-xl p-2 pl-3"
                >
                  <button
                    type="button"
                    data-testid={`cycle-variant-${p.id}`}
                    onClick={() => cyclePupilVariant(p.id)}
                    title="Tap to change ship"
                    className="rounded-lg hover:bg-[#1C204B] transition-colors shrink-0"
                  >
                    <Rocket color={p.color} variant={p.variant} size={36} flame={false} />
                  </button>
                  <Input
                    data-testid={`rename-pupil-${p.id}`}
                    value={p.name}
                    onChange={(e) => setPupilName(p.id, e.target.value)}
                    className="flex-1 min-w-[80px] h-9 bg-[#141738] border-[#252B5C] text-white font-heading font-semibold"
                  />
                  <select
                    data-testid={`stage-select-${p.id}`}
                    value={p.stageIndex}
                    onChange={(e) => setPupilStage(p.id, Number(e.target.value))}
                    className="bg-[#141738] border border-[#252B5C] rounded-md h-9 px-1 text-xs text-white max-w-[110px]"
                    title="Set position"
                  >
                    {room.stages.map((s, i) => (
                      <option key={i} value={i}>
                        {s.icon} {s.name}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-1 shrink-0">
                    {ROCKET_COLORS.map((c) => (
                      <button
                        key={c.hex}
                        onClick={() => setPupilColor(p.id, c.hex)}
                        className={`w-5 h-5 rounded-full border-2 ${
                          p.color === c.hex ? "border-white" : "border-transparent"
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                  <Button
                    data-testid={`delete-pupil-${p.id}`}
                    size="icon"
                    variant="ghost"
                    onClick={() => removePupil(p.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* STAGES */}
          <TabsContent value="stages" className="space-y-4 pt-4">
            <div className="flex flex-wrap items-center gap-2 bg-[#0E1126] border border-[#252B5C] rounded-xl p-2">
              <span className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Editing project</span>
              <select
                data-testid="stage-project-select"
                value={room.activeProjectId}
                onChange={async (e) => { const d = await setActiveProject(room.id, e.target.value); onRoom && onRoom(d); }}
                className="bg-[#141738] border border-[#252B5C] rounded-md h-9 px-2 text-white font-heading"
              >
                {(room.projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span className="text-xs uppercase tracking-wider text-cyan-400 font-semibold ml-2">Colour</span>
              <div className="flex gap-1" data-testid="project-color-picker">
                {["#3B82F6", "#EC4899", "#10B981", "#F59E0B", "#A855F7", "#06B6D4", "#EF4444", "#8B5CF6"].map((hex) => {
                  const active = (room.projects || []).find((p) => p.id === room.activeProjectId)?.color === hex;
                  return (
                    <button
                      key={hex}
                      data-testid={`project-color-${hex.slice(1)}`}
                      onClick={async () => { const d = await updateProject(room.id, room.activeProjectId, { color: hex }); onRoom && onRoom(d); }}
                      className={`w-7 h-7 rounded-full border-2 ${active ? "border-white scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: hex }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={newStageIcon}
                onChange={(e) => setNewStageIcon(e.target.value)}
                className="bg-[#0E1126] border border-[#252B5C] rounded-md h-10 px-2 text-lg"
              >
                {STAGE_ICONS.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
              <Input
                data-testid="stage-name-input"
                value={newStage}
                onChange={(e) => setNewStage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addStage()}
                placeholder="New stage / finish line name"
                className="flex-1 min-w-[160px] bg-[#0E1126] border-[#252B5C] text-white"
              />
              <Button
                data-testid="add-stage-btn"
                onClick={addStage}
                className="bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] min-h-[44px]"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="space-y-2">
              {room.stages.map((s, idx) => (
                <div
                  key={s.id || idx}
                  data-testid={`manage-stage-${idx}`}
                  className="flex items-center gap-2 bg-[#0E1126] border border-[#252B5C] rounded-xl p-2"
                >
                  <select
                    value={s.icon}
                    onChange={(e) => setStageIcon(idx, e.target.value)}
                    className="bg-[#141738] border border-[#252B5C] rounded-md h-9 px-1 text-lg"
                  >
                    {STAGE_ICONS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                  <Input
                    value={s.name}
                    onChange={(e) => renameStage(idx, e.target.value)}
                    className="flex-1 bg-[#141738] border-[#252B5C] text-white h-9"
                  />
                  <span className="text-xs text-slate-500 w-6 text-center">{idx + 1}</span>
                  <Button size="icon" variant="ghost" onClick={() => moveStage(idx, -1)} className="text-slate-300 h-8 w-8">
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => moveStage(idx, 1)} className="text-slate-300 h-8 w-8">
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-stage-${idx}`}
                    size="icon"
                    variant="ghost"
                    onClick={() => removeStage(idx)}
                    disabled={room.stages.length <= 2}
                    className="text-red-400 hover:bg-red-500/10 h-8 w-8 disabled:opacity-30"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TEST */}
          <TabsContent value="test" className="space-y-3 pt-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-slate-300">Exploration Test for <b className="text-cyan-300">{activeProject.name}</b> — {questions.length}/10</p>
              <Button data-testid="add-question-btn" onClick={addQuestion} disabled={questions.length >= 10} className="bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] min-h-[40px] disabled:opacity-40">
                <Plus className="w-4 h-4 mr-1" /> Question
              </Button>
            </div>
            <div className="flex items-center gap-2 bg-[#0E1126] border border-[#252B5C] rounded-xl p-3">
              <span className="text-sm text-slate-300">Attempts allowed per pupil</span>
              <select data-testid="attempts-allowed-select" value={activeProject.attemptsAllowed || 0} onChange={(e) => saveAttempts(Number(e.target.value))} className="bg-[#141738] border border-[#252B5C] rounded-md h-9 px-2 text-white ml-auto">
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={0}>Unlimited</option>
              </select>
            </div>
            {questions.map((q, qi) => (
              <div key={q.id} data-testid={`edit-question-${qi}`} className="bg-[#0E1126] border border-[#252B5C] rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input data-testid={`question-text-${qi}`} defaultValue={q.text} onBlur={(e) => patchQuestion(q.id, { text: e.target.value })} className="flex-1 bg-[#141738] border-[#252B5C] text-white h-9" />
                  <span className="text-xs text-slate-400">marks</span>
                  <Input data-testid={`question-marks-${qi}`} type="number" min="1" defaultValue={q.marks} onBlur={(e) => patchQuestion(q.id, { marks: Math.max(1, Number(e.target.value) || 1) })} className="w-14 bg-[#141738] border-[#252B5C] text-white h-9" />
                  <label className="cursor-pointer text-slate-300 hover:text-white" title="Add picture"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && fileToDataUrl(e.target.files[0], (url) => patchQuestion(q.id, { image: url }))} /><ImageIcon className="w-4 h-4" /></label>
                  <Button size="icon" variant="ghost" onClick={() => removeQuestion(q.id)} className="text-red-400 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                </div>
                {q.image && <div className="flex items-center gap-2"><img src={q.image} alt="" className="h-16 rounded" /><Button size="sm" variant="ghost" onClick={() => patchQuestion(q.id, { image: null })} className="text-red-400 text-xs">remove pic</Button></div>}
                <div className="space-y-1">
                  {q.options.map((o, oi) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <button data-testid={`option-correct-${qi}-${oi}`} onClick={() => patchOption(q.id, o.id, { correct: !o.correct })} className={`w-6 h-6 rounded border flex items-center justify-center text-xs shrink-0 ${o.correct ? "bg-emerald-500 border-emerald-500 text-[#0B0D1B]" : "border-slate-500 text-slate-400"}`}>{o.correct ? "✓" : ""}</button>
                      <Input data-testid={`option-text-${qi}-${oi}`} defaultValue={o.text} onBlur={(e) => patchOption(q.id, o.id, { text: e.target.value })} className="flex-1 bg-[#141738] border-[#252B5C] text-white h-8" />
                      {o.image && <img src={o.image} alt="" className="h-7 rounded" />}
                      <label className="cursor-pointer text-slate-300 hover:text-white"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && fileToDataUrl(e.target.files[0], (url) => patchOption(q.id, o.id, { image: url }))} /><ImageIcon className="w-4 h-4" /></label>
                      <Button size="icon" variant="ghost" onClick={() => removeOption(q.id, o.id)} className="text-red-400 h-7 w-7"><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  <Button data-testid={`add-option-${qi}`} size="sm" variant="ghost" onClick={() => addOption(q.id)} className="text-cyan-300 text-xs"><Plus className="w-3 h-3 mr-1" />option</Button>
                </div>
                <p className="text-[10px] text-slate-500">Tick every correct answer (pictures optional). Full marks only if the pupil picks exactly those.</p>
              </div>
            ))}
            {questions.length === 0 && <p className="text-slate-400 text-sm">Add up to 10 multiple-choice questions. Pupils take this when their ship reaches the galaxy. Pass mark is over 40%.</p>}
          </TabsContent>

          {/* RUBRIC */}
          <TabsContent value="rubric" className="space-y-3 pt-4">
            <p className="text-sm text-slate-300">Standards-based rubric for <b className="text-cyan-300">{activeProject.name}</b>. Give each standard a title, then describe what a pupil needs to reach each level. Marks stay teacher-only (hidden from pupils) and appear in the Class Report.</p>
            {criteria.map((c, ci) => (
              <div key={c.id} data-testid={`edit-criterion-${ci}`} className="bg-[#0E1126] border border-[#252B5C] rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-300 font-heading shrink-0">Standard</span>
                  <Input data-testid={`criterion-text-${ci}`} defaultValue={c.text} onBlur={(e) => patchCriterion(c.id, e.target.value)} className="flex-1 bg-[#141738] border-[#252B5C] text-white h-9" />
                  <Button size="icon" variant="ghost" onClick={() => removeCriterion(c.id)} className="text-red-400 h-8 w-8"><X className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-1.5">
                  {(c.levels || []).map((lv, li) => (
                    <div key={lv.id} data-testid={`criterion-${ci}-level-${li}`} className="flex items-start gap-2">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-400 text-cyan-200 flex items-center justify-center text-xs font-bold mt-0.5">{lv.points}</span>
                      <Input data-testid={`level-label-${ci}-${li}`} defaultValue={lv.label} onBlur={(e) => patchLevel(c.id, lv.id, { label: e.target.value })} className="w-28 sm:w-32 shrink-0 bg-[#141738] border-[#252B5C] text-white h-8" />
                      <Input data-testid={`level-descriptor-${ci}-${li}`} defaultValue={lv.descriptor} placeholder="What a pupil needs to achieve this…" onBlur={(e) => patchLevel(c.id, lv.id, { descriptor: e.target.value })} className="flex-1 bg-[#141738] border-[#252B5C] text-white h-8" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Button data-testid="add-criterion-btn" onClick={addCriterion} className="bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] min-h-[40px]"><Plus className="w-4 h-4 mr-1" /> Add standard</Button>
          </TabsContent>

          {/* QUICK TOOLS */}
          <TabsContent value="quick" className="space-y-3 pt-4">
            <p className="text-slate-300 text-sm">Move the whole class at once:</p>
            <div className="flex flex-wrap gap-2">
              {room.stages.map((s, idx) => (
                <Button
                  key={idx}
                  data-testid={`move-all-stage-${idx}`}
                  onClick={() => moveAll(idx)}
                  className="bg-[#0E1126] hover:bg-[#1C204B] border border-[#252B5C] min-h-[44px]"
                >
                  {s.icon} {s.name}
                </Button>
              ))}
            </div>
            <Button
              data-testid="reset-board-btn"
              onClick={() => moveAll(0)}
              variant="outline"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] min-h-[44px]"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reset all rockets to the launchpad
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
